import axios from "axios";
import { captureTracking } from "./applicationAttribution";
import { experimentProperties } from "./experiments";
import { getConsent, onConsentChange, pushDefaultConsent, replayConsent } from "./consent";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;
// Verified against scripts/build_gtm_container.py and exports/README_GTM_IMPORT.md.
// Live GTM-KP6L3KH v6 has NO GA4 tags. This module is the sole GA4 event owner.
// Remove this direct configuration before publishing GA4 tags in that container.
const GA4_ID = (process.env.REACT_APP_GA4_ID || "G-HRQR7E2MDG").trim();
const GADS_ID = (process.env.REACT_APP_GADS_ID || "").trim();
const canTrack = () => typeof window !== "undefined" && getConsent() === "granted";

// Only public, known route names leave the app. Never send query strings, hashes,
// customer tokens, admin paths, arbitrary slugs, page titles or referrers.
const PUBLIC_PATHS = new Set([
  "/", "/growth-points", "/growth-points/catalog", "/apply", "/results", "/resources",
  "/seo-report", "/marketing-audit-free", "/possibility-lattice", "/prediction-engine",
  "/revenue-masterclass", "/utilities/seo-report", "/utilities/marketing-audit",
  "/utilities/strategy-engine", "/utilities/prediction-engine", "/about-bow-tie-kreative",
  "/blog", "/docs", "/locations", "/industries", "/case-studies", "/support",
  "/terms-and-conditions", "/refund-policy", "/privacy-policy", "/cookie-policy",
  "/email/unsubscribe", "/cancel-subscription",
]);
export const safePagePath = (path) => {
  const pathname = String(path || "/").split(/[?#]/)[0];
  if (PUBLIC_PATHS.has(pathname)) return pathname;
  if (/^\/apply\/(status|booked)\//.test(pathname)) return pathname.split("/").slice(0, 3).join("/") + "/:token";
  const publicSlug = pathname.match(/^\/(blog|docs|locations|industries|support|forms|booking)\//);
  if (publicSlug) return `/${publicSlug[1]}/:slug`;
  return "/other";
};

// Deliberately narrow allowlist: arbitrary labels, URLs, names, IDs, nested
// metadata and error text can all contain PII, even under innocuous field names.
export const sanitizeProperties = (properties = {}) => {
  const clean = {};
  if (!properties || typeof properties !== "object") return clean;
  for (const key of ["value", "percent", "count", "duration"]) {
    if (typeof properties[key] === "number" && Number.isFinite(properties[key])) clean[key] = properties[key];
  }
  if (typeof properties.currency === "string" && /^[A-Z]{3}$/.test(properties.currency)) clean.currency = properties.currency;
  for (const key of ["conversion", "has_phone"]) {
    if (typeof properties[key] === "boolean") clean[key] = properties[key];
  }
  for (const key of ["experiment_id", "variant_id", "assignment_id", "event_id", "product_id", "application_type", "form_id", "segment", "ad_id"]) {
    if (typeof properties[key] === "string" && /^[a-zA-Z0-9_-]{1,100}$/.test(properties[key])) clean[key] = properties[key];
  }
  return clean;
};
const pageProperties = (path) => ({
  page_path: safePagePath(path),
  page_location: `https://bowtiekreative.com${safePagePath(path)}`,
  page_title: "Bow Tie Kreative",
  page_referrer: "",
});
const validEvent = (event) => typeof event === "string" && /^[a-z][a-z0-9_]{0,39}$/.test(event);

export const campaignProperties = () => {
  const tracking = captureTracking();
  const fields = { utm_source: "campaign_source", utm_medium: "campaign_medium", utm_campaign: "campaign_name", utm_id: "campaign_id", utm_content: "campaign_content", utm_term: "campaign_term" };
  return Object.fromEntries(Object.entries(fields).filter(([key]) => typeof tracking[key] === "string" && /^[a-zA-Z0-9_.-]{1,120}$/.test(tracking[key])).map(([key, target]) => [target, tracking[key]]));
};
let ga4Loaded = false;
export const initGA4 = () => {
  if (typeof window === "undefined") return;
  pushDefaultConsent();
  if (!canTrack() || !/^G-[A-Z0-9]+$/.test(GA4_ID) || ga4Loaded) return;
  replayConsent();
  // index.html already loads gtag.js for Ads. Reuse that loader and gtag queue.
  if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
  }
  window.gtag("config", GA4_ID, {
    send_page_view: false,
    ...campaignProperties(),
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...pageProperties(window.location.pathname),
  });
  ga4Loaded = true;
};

/** One direct GA4 event; do NOT also push an event object for GTM to re-send. */
export const trackEvent = (event, extra = {}) => {
  if (!canTrack() || !validEvent(event)) return;
  initGA4();
  const metadata = sanitizeProperties({ ...experimentProperties(), ...extra.metadata });
  const page = pageProperties(window.location.pathname);
  try {
    axios.post(`${API}/analytics/event`, { event, page: page.page_path, metadata }).catch(() => {});
  } catch (_) { /* analytics must never interrupt the UI */ }
  if (ga4Loaded) window.gtag("event", event, { ...metadata, ...page, send_to: GA4_ID });
};

// GTM owns init + initial Meta PageView. Keep state outside React so StrictMode
// and remounts do not manufacture another initial page. Consent grant releases
// Meta's own revoked queue; never manually re-send that initial PageView.
let lastMetaPath = typeof window !== "undefined" ? window.location.pathname : "/";
let lastGAPath = null;
export const trackPageView = (path) => {
  if (!canTrack()) return;
  initGA4();
  const route = String(path || "/").split(/[?#]/)[0];
  if (ga4Loaded && route !== lastGAPath) {
    window.gtag("event", "page_view", { ...pageProperties(route), send_to: GA4_ID });
    lastGAPath = route;
  }
  if (route !== lastMetaPath && typeof window.fbq === "function") {
    window.fbq("track", "PageView");
    lastMetaPath = route;
  }
};
onConsentChange((value) => {
  if (value === "granted") trackPageView(window.location.pathname);
  else lastGAPath = null;
});

// Explicit completed-action names only. Clicks/attempts/unknown events never
// become successful registrations, leads or purchases by substring matching.
const META_EVENTS = {
  purchase: "Purchase", purchase_completed: "Purchase",
  checkout: "InitiateCheckout", checkout_start: "InitiateCheckout", checkout_started: "InitiateCheckout", begin_checkout: "InitiateCheckout",
  signup_completed: "CompleteRegistration", sign_up: "CompleteRegistration", registration_completed: "CompleteRegistration",
  generate_lead: "Lead", lead_submit: "Lead", contact_submit: "Lead", consultation_submit: "Lead",
  newsletter_signup: "Lead", mailing_list_subscribed: "Lead",
  booking_completed: "Schedule", calendar_booking_complete: "Schedule",
  view_content: "ViewContent", product_view: "ViewContent",
};
export const trackConversion = (name, value = null, currency = "USD", extra = {}) => {
  if (!canTrack() || !validEvent(name)) return;
  const payload = sanitizeProperties({ ...extra, value, currency });
  trackEvent(name, { metadata: payload });
  if (typeof window.fbq === "function") {
    const standard = META_EVENTS[name];
    const options = payload.event_id ? { eventID: payload.event_id } : undefined;
    window.fbq(standard ? "track" : "trackCustom", standard || name, payload, options);
  }
  // A bare AW account ID is not a conversion action. Only explicit, labeled
  // actions for confirmed outcomes qualify; no duplicate human-readable send.
  if (/^AW-\d+\/[A-Za-z0-9_-]+$/.test(GADS_ID) && ["Purchase", "Lead", "CompleteRegistration", "Schedule"].includes(META_EVENTS[name])) {
    window.gtag("event", "conversion", { ...payload, send_to: GADS_ID });
  }
  // Browser CAPI forwarding intentionally disabled: the old bridge sent two
  // events, included raw PII and supplied no event_id for Pixel deduplication.
  // Server-side leads are sent only by the accepted application endpoint.
};
